import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteNotification,
  getNotificationAutoTriggers,
  getNotificationCategories,
  getNotificationsListList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateNotifBunch,
} from "store/outAppNotifications/slice";
import { notificationsListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import NotificationsFilter from "../NotificationsFilter";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import formatCamelCase from "helpers/formatCamelCase";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const PushList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
  }>({
    title: "none",
    isActive: "none",
  });
  const [currentLimit, setLimit] = useState<number>(notificationsListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([]);
  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store["out-notif"].list);
  const isListFetched = useAppSelector(
    (store) => store["out-notif"].isListFetched,
  );
  const totalItems = useAppSelector((store) => store["out-notif"].totalItems);
  const totalPages = useAppSelector((store) => store["out-notif"].totalPages);
  const activePage = useAppSelector((store) => store["out-notif"].activePage);
  const selectedItems = useAppSelector(
    (store) => store["out-notif"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store["out-notif"].processingItemId,
  );

  const categories = useAppSelector((store) => store["out-notif"].categories);
  const triggers = useAppSelector((store) => store["out-notif"].triggers);

  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'out-notif',
    isListFetched
  });

  useEffect(() => {
    setSearch("");
    setActive([{ value: 3, label: "All" }]);
    setOrder({ title: "none", isActive: "none" });
    dispatch(handlePaginate(1));
  }, [location.pathname]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getNotificationsListList({
          limit: currentLimit,
          offset: activePage - 1,
          type: location.pathname.includes("auto") ? "auto" : "manual",
          title: search,

          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,

          category:
            selectedCategories.length > 0
              ? selectedCategories.map((el) => String(el.value))
              : undefined,

          trigger:
            selectedTriggers.length > 0
              ? selectedTriggers.map((el) => String(el.value))
              : undefined,

          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, location.pathname, order]);

  const [selectedCategories, setSelectedCategories] = useState<
    { value: string | number; label: string }[]
  >([]);

  const [selectedTriggers, setSelectedTriggers] = useState<
    { value: string | number; label: string }[]
  >([]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    if (!permissions?.update) return;

    try {
      await dispatch(
        updateNotifBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.notification}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.notification }));
  };
  const handleOpenCreateModal = () => {
    if (!permissions?.create) return;

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.notification}&type=bet`,
    );
    dispatch(openModal({ activeModal: ModalTypes.notification }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    if (!permissions?.delete) return;

    const result = await dispatch(deleteNotification(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const isAuto = location.pathname.includes("auto");
  const isEmail = location.pathname.includes("email");

  const emptyMessage = isEmail
    ? isAuto
      ? t("noAutoEmailsFound")
      : t("noEmailsFound")
    : isAuto
      ? t("noAutoPushNotificationsFound")
      : t("noPushNotificationsFound");

  const formattedList = list.map((item) => ({
    id: item.id,
    title: item.title,
    content: item.content,
    category: item.category || "-",
    ...(isAuto && {
      trigger: item.trigger ? formatCamelCase(item.trigger) : "-",
    }),
    type: item.type,
    isActive: item.isActive,
    mobileImage: item.mobileImage,
  }));

  useEffect(() => {
    dispatch(getNotificationCategories());
    dispatch(getNotificationAutoTriggers());
  }, []);

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <NotificationsFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
        categories={categories}
        triggers={triggers}
        selectedCategories={selectedCategories}
        handleUpdateCategories={setSelectedCategories}
        selectedTriggers={selectedTriggers}
        handleUpdateTriggers={setSelectedTriggers}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addNotification")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={7} rowsCount={2} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 && (
              <EmptyState message={t("noNotificationsFound")} />
            )}

            {list.length > 0 && (
              <Table
                data={formattedList}
                idField="id"
                selectedItems={selectedItems}
                hasCheckbox={permissions?.update ?? false}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onDelete={(id) => handleDeleteItem(id)}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                processingId={processingItemId}
                columns={{
                  hiddenColumns: ["mobileImage"],
                }}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={notificationsListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default PushList;
